import { PDFDocument, PDFPage, StandardFonts } from 'pdf-lib';

import { mapStampOrigin, type PageRect, type StampSize } from './coordinate-mapper';
import { normalizeEndorsement, type BatesRange, type StampingConfig } from './models';
import { renderStampText } from './text-renderer';

const DEFAULT_FONT_SIZE = 12;
const HORIZONTAL_PADDING = 10;
const VERTICAL_PADDING = 5;

type BakePageResult = {
  pageBounds: PageRect;
  stampOrigin: { x: number; y: number };
  stampSize: StampSize;
  stampedText: string;
};

export async function bakePages(
  pdf: PDFDocument,
  config: StampingConfig,
  range: BatesRange,
): Promise<{ document: PDFDocument; results: BakePageResult[] }> {
  const pages = pdf.getPages();
  const font = await pdf.embedFont(StandardFonts.Helvetica);

  const results: BakePageResult[] = [];

  for (const [pageIndex, page] of pages.entries()) {
    const stampedText = buildStampedText(config, range.startNumber + pageIndex);
    const pageBounds = getPageRect(page);
    const stampSize = measureStampSize(font, stampedText, DEFAULT_FONT_SIZE);
    const stampOrigin = mapStampOrigin(pageBounds, config.position, stampSize);

    await renderStampText({
      page,
      text: stampedText,
      x: stampOrigin.x,
      y: stampOrigin.y,
      width: stampSize.width,
      height: stampSize.height,
      opacity: config.opacity,
      theme: config.theme,
      fontSize: DEFAULT_FONT_SIZE,
      horizontalPadding: HORIZONTAL_PADDING,
      verticalPadding: VERTICAL_PADDING,
    });

    results.push({ pageBounds, stampOrigin, stampSize, stampedText });
  }

  return { document: pdf, results };
}

function buildStampedText(config: StampingConfig, number: number): string {
  const bates = `${config.prefix}${number.toString().padStart(config.digitCount, '0')}`;
  const endorsement = normalizeEndorsement(config.endorsement);
  return endorsement ? `${bates}\n${endorsement}` : bates;
}

function getPageRect(page: PDFPage): PageRect {
  const { width, height } = page.getSize();
  return { minX: 0, minY: 0, maxX: width, maxY: height };
}

function measureStampSize(font: Awaited<ReturnType<PDFDocument['embedFont']>>, text: string, fontSize: number): StampSize {
  const lines = text.split('\n');
  const textWidth = lines.reduce((max, line) => Math.max(max, font.widthOfTextAtSize(line, fontSize)), 0);
  const lineHeight = font.heightAtSize(fontSize);
  const textHeight = lineHeight * lines.length;

  return {
    width: textWidth + HORIZONTAL_PADDING * 2,
    height: textHeight + VERTICAL_PADDING * 2,
  };
}
